/**
 * TUVI - Componente PDF de Adicional
 * Renderiza el adicional en formato HTML para generar PDF
 * Patron basado en CotizacionPDF.jsx (jsPDF + html2canvas)
 */

import React from 'react';

const formatCurrency = (value) => {
  if (!value && value !== 0) return '$0';
  return new Intl.NumberFormat('es-CO', {
    style: 'currency', currency: 'COP',
    minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(value);
};

const OBSERVACIONES_ADICIONAL = [
  'Los precios de la presente propuesta de adicional tienen una validez de 30 dias calendario a partir de la fecha de expedicion.',
  'Los valores presentados incluyen Administracion, Imprevistos y Utilidad (AIU) e IVA.',
  'Las actividades adicionales se ejecutaran bajo las mismas condiciones de calidad y garantia del contrato original.',
  'TUVI garantiza todos los trabajos realizados por un periodo de un (1) ano contado a partir de la fecha de entrega.'
];

const OBSERVACIONES_RETIRO = [
  'El presente documento formaliza el retiro de actividades del alcance original del proyecto.',
  'Los valores presentados incluyen Administracion, Imprevistos y Utilidad (AIU) e IVA que seran descontados.',
  'El retiro de actividades se realizara de acuerdo a lo pactado con el propietario.',
  'TUVI garantiza que las actividades restantes se ejecutaran bajo las mismas condiciones de calidad del contrato original.'
];

const AdicionalPDF = ({ adicional }) => {
  if (!adicional) return null;

  const {
    consecutivo, fecha, descripcion, tipo,
    nombre_cliente, cedula_cliente, telefono_cliente, correo_cliente,
    proyecto_nombre, area_m2, apartamento,
    subtotal, aiu_valor, iva_valor, valor_total,
    valor_proyecto_anterior, valor_proyecto_actualizado,
    actividades, notas, dias_adicionales, fecha_entrega_actual
  } = adicional;

  const esRetiro = tipo === 'retiro';

  // Cálculo de la nueva fecha de entrega = fecha actual + días HÁBILES (lun-vie)
  const formatFecha = (d) => {
    if (!d) return null;
    try {
      const dt = new Date(d.length === 10 ? d + 'T12:00:00' : d);
      return dt.toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch { return null; }
  };
  const sumarDiasHabiles = (d, n) => {
    if (!d || !n) return null;
    try {
      const dt = new Date(d.length === 10 ? d + 'T12:00:00' : d);
      let restantes = Number(n);
      while (restantes > 0) {
        dt.setDate(dt.getDate() + 1);
        const dow = dt.getDay(); // 0 = dom, 6 = sab
        if (dow !== 0 && dow !== 6) restantes--;
      }
      return dt.toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch { return null; }
  };
  const dias = Number(dias_adicionales) || 0;
  const fechaActualLbl = formatFecha(fecha_entrega_actual);
  const fechaNuevaLbl = sumarDiasHabiles(fecha_entrega_actual, dias);
  const mostrarBloqueDias = !esRetiro && dias > 0;

  // Agrupar actividades por espacio
  const actividadesPorEspacio = {};
  (actividades || []).forEach(act => {
    const esp = act.espacio || 'GENERALES';
    if (!actividadesPorEspacio[esp]) actividadesPorEspacio[esp] = [];
    actividadesPorEspacio[esp].push(act);
  });

  const espaciosOrdenados = Object.keys(actividadesPorEspacio);
  let itemGlobal = 0;

  return (
    <div>
      <div className="adicional-page">
        {/* Banner */}
        <div className="pdf-banner">
          <img src="/logo_tuvi_banner.jpg" alt="TUVI" className="pdf-banner-logo" crossOrigin="anonymous" />
          <span className="pdf-banner-title">{esRetiro ? 'RETIRO' : 'ADICIONAL'}</span>
        </div>

        <div style={{ padding: '65px 65px 0' }}>
          {/* Numero y fecha */}
          <div className="pdf-fecha-cotizacion">
            <span className="pdf-fecha-lbl">{esRetiro ? 'Retiro' : 'Adicional'} No. {consecutivo}</span>
            <span className="pdf-fecha-val">{fecha}</span>
          </div>

          {/* Datos del Propietario e Inmueble */}
          <div className="pdf-datos-wrapper">
            <div className="pdf-datos-left">
              <table className="pdf-datos-block">
                <thead>
                  <tr><th colSpan="2" className="pdf-datos-title-dark">Datos del Propietario</th></tr>
                </thead>
                <tbody>
                  <tr><td className="pdf-dato-lbl">Nombre</td><td className="pdf-dato-val">{nombre_cliente}</td></tr>
                  <tr><td className="pdf-dato-lbl">Cedula</td><td className="pdf-dato-val">{cedula_cliente}</td></tr>
                  <tr><td className="pdf-dato-lbl">Telefono</td><td className="pdf-dato-val">{telefono_cliente}</td></tr>
                  <tr><td className="pdf-dato-lbl">Correo</td><td className="pdf-dato-val">{correo_cliente}</td></tr>
                </tbody>
              </table>
            </div>

            <div className="pdf-datos-right">
              <table className="pdf-datos-block">
                <thead>
                  <tr><th colSpan="4" className="pdf-datos-title-dark">Datos del Inmueble</th></tr>
                </thead>
                <tbody>
                  <tr><td className="pdf-dato-lbl">Proyecto</td><td colSpan="3" className="pdf-dato-val">{proyecto_nombre}</td></tr>
                  <tr className="pdf-separator-row"><td colSpan="4"></td></tr>
                  <tr>
                    <td className="pdf-dato-lbl">Area</td><td className="pdf-dato-val">{area_m2} m2</td>
                    <td className="pdf-dato-lbl">Apartamento</td><td className="pdf-dato-val">{apartamento}</td>
                  </tr>
                  {mostrarBloqueDias && (
                    <>
                      <tr className="pdf-separator-row"><td colSpan="4"></td></tr>
                      <tr>
                        <td colSpan="2" className="pdf-dato-lbl">Dias habiles adicionales</td>
                        <td colSpan="2" className="pdf-dato-val">{dias} dia{dias === 1 ? '' : 's'} habil{dias === 1 ? '' : 'es'}</td>
                      </tr>
                      <tr>
                        <td colSpan="2" className="pdf-dato-lbl">Entrega anterior</td>
                        <td colSpan="2" className="pdf-dato-val">{fechaActualLbl || 'Sin fecha cronograma'}</td>
                      </tr>
                      <tr>
                        <td colSpan="2" className="pdf-dato-lbl" style={{ background: '#f5e6c8', fontWeight: 700 }}>Nueva fecha de entrega</td>
                        <td colSpan="2" className="pdf-dato-val" style={{ background: '#f5e6c8', fontWeight: 700, color: '#1A2432' }}>{fechaNuevaLbl || 'Cargar fecha en el cronograma'}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Descripcion del adicional */}
          {descripcion && (
            <div className="cotizacion-pdf-section" style={{ marginTop: '10px' }}>
              <h4 className="pdf-section-beige">{esRetiro ? 'DESCRIPCION DEL RETIRO' : 'DESCRIPCION DEL ADICIONAL'}</h4>
              <p style={{ fontSize: '9pt', padding: '5px 10px', margin: 0 }}>{descripcion}</p>
            </div>
          )}

          {/* Tabla de actividades */}
          <div className="cotizacion-pdf-section" style={{ marginTop: '10px', marginBottom: '5px' }}>
            <h4 className="pdf-section-beige">{esRetiro ? 'ACTIVIDADES A RETIRAR' : 'ACTIVIDADES ADICIONALES'}</h4>
          </div>
          <table className="pdf-actividades-table">
            <thead>
              <tr>
                <th style={{ width: '30px' }}>Item</th>
                <th>Descripcion</th>
                <th style={{ width: '35px' }}>Und</th>
                <th style={{ width: '35px' }}>Cant</th>
                <th style={{ width: '80px' }} className="text-right">V. Total</th>
              </tr>
            </thead>
            <tbody>
              {espaciosOrdenados.map((espacio, gIdx) => {
                const acts = actividadesPorEspacio[espacio] || [];
                const rows = [];
                const totalEspacio = acts.reduce((sum, act) => sum + (parseFloat(act.valor_total) || 0), 0);

                if (gIdx > 0) {
                  rows.push(
                    <tr key={`sep1-${gIdx}`} className="pdf-espacio-separator"><td colSpan="5"></td></tr>,
                    <tr key={`sep2-${gIdx}`} className="pdf-espacio-separator"><td colSpan="5"></td></tr>
                  );
                }
                rows.push(
                  <tr key={`esp-${gIdx}`} className="espacio-header">
                    <td colSpan="4">{espacio}</td>
                    <td className="text-right">{formatCurrency(totalEspacio)}</td>
                  </tr>
                );
                acts.forEach((act, aIdx) => {
                  itemGlobal++;
                  rows.push(
                    <tr key={`act-${gIdx}-${aIdx}`} className={aIdx % 2 === 1 ? 'pdf-act-row-beige' : ''}>
                      <td>{itemGlobal}</td>
                      <td>{act.descripcion}</td>
                      <td>{act.unidad}</td>
                      <td>{act.unidad === 'UND' ? act.cantidad : ''}</td>
                      <td className="text-right">{formatCurrency(act.valor_total)}</td>
                    </tr>
                  );
                });
                return rows;
              })}
              <tr className="subtotal-row">
                <td colSpan="4" className="text-right">SUBTOTAL</td>
                <td className="text-right">{formatCurrency(subtotal)}</td>
              </tr>
            </tbody>
          </table>

          {/* Resumen financiero */}
          <div className="cotizacion-pdf-section" style={{ marginTop: '15px' }}>
            <h4 className="pdf-section-beige">RESUMEN FINANCIERO</h4>
            <table className="pdf-financial-table">
              <tbody>
                <tr><td>Subtotal</td><td className="text-right">{formatCurrency(subtotal)}</td></tr>
                <tr><td>AIU (15%)</td><td className="text-right">{formatCurrency(aiu_valor)}</td></tr>
                <tr><td>IVA</td><td className="text-right">{formatCurrency(iva_valor)}</td></tr>
                <tr className="valor-final-row">
                  <td>{esRetiro ? 'VALOR TOTAL RETIRO' : 'VALOR TOTAL ADICIONAL'}</td>
                  <td className="text-right">{esRetiro ? '-' : ''}{formatCurrency(Math.abs(valor_total))}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Comparativo de valores */}
          <div className="cotizacion-pdf-section" style={{ marginTop: '15px' }}>
            <h4 className="pdf-section-beige">COMPARATIVO DE VALORES</h4>
            <table className="pdf-financial-table">
              <tbody>
                <tr>
                  <td>Valor Anterior del Proyecto</td>
                  <td className="text-right">{formatCurrency(valor_proyecto_anterior)}</td>
                </tr>
                <tr>
                  <td>{esRetiro ? 'Valor del Retiro' : 'Valor del Adicional'}</td>
                  <td className="text-right">{esRetiro ? '-' : ''}{formatCurrency(Math.abs(valor_total))}</td>
                </tr>
                <tr className="valor-final-row">
                  <td>VALOR ACTUALIZADO DEL PROYECTO</td>
                  <td className="text-right">{formatCurrency(valor_proyecto_actualizado)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Notas */}
          {notas && (
            <div className="cotizacion-pdf-section" style={{ marginTop: '10px' }}>
              <h4 className="pdf-section-beige">NOTAS</h4>
              <p style={{ fontSize: '9pt', padding: '5px 10px', margin: 0 }}>{notas}</p>
            </div>
          )}

          {/* Observaciones */}
          <div className="cotizacion-pdf-section pdf-section-small" style={{ marginTop: '10px' }}>
            <h4 className="pdf-section-beige">OBSERVACIONES</h4>
            <div className="pdf-observaciones">
              {(esRetiro ? OBSERVACIONES_RETIRO : OBSERVACIONES_ADICIONAL).map((obs, idx) => (
                <p key={idx}>&#8226; {obs}</p>
              ))}
            </div>
          </div>

          {/* Firma */}
          <div style={{ marginTop: '60px', paddingBottom: '40px', display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: '280px', textAlign: 'center' }}>
              <div style={{ borderBottom: '1px solid #333', marginBottom: '8px', height: '60px' }}></div>
              <p style={{ fontSize: '9pt', margin: 0, fontWeight: 'bold' }}>Firma del Propietario</p>
              <p style={{ fontSize: '8pt', margin: '2px 0 0', color: '#666' }}>{nombre_cliente}</p>
              <p style={{ fontSize: '8pt', margin: '2px 0 0', color: '#666' }}>C.C. {cedula_cliente}</p>
            </div>
          </div>
        </div>

        {/* Imagen final */}
        <div className="pdf-imagen-final">
          <img src="/final_cotizador.png" alt="TUVI" crossOrigin="anonymous" />
        </div>
      </div>
    </div>
  );
};

export default AdicionalPDF;
